import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, FileText, ClipboardList, PackageSearch, ShieldAlert, CalendarRange, Plus } from "lucide-react";
import StatusBadge from "./StatusBadge.jsx";
import { fmtDate, fmtRelative } from "../lib/format.js";

// Visual workflow for a SOM with its linked PCPs, PECs, SWCs (Garantias) and active MPM plans.
// Single-row layout: SOM → PCP → PEC | SWC | MPM (arrows only on the sequential edges).
export default function WorkflowTimeline({ som }) {
  const pcps = som.linked_pcps || [];
  const pecs = som.linked_pecs || [];
  const swcs = som.linked_swcs || [];
  const mpms = som.active_mpm_plans || [];

  return (
    <div
      className="bg-white border border-gray-200 rounded-md p-4 mb-4"
      data-testid="som-workflow-timeline"
    >
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">
          Fluxo de trabalho
        </h3>
        <span
          className="uppercase tracking-widest text-[#6C757D] whitespace-nowrap"
          style={{ fontSize: "11px", letterSpacing: "0.1em" }}
        >
          {pcps.length} PCP · {pecs.length} PEC · {swcs.length} garantia{swcs.length === 1 ? "" : "s"} · {mpms.length} MPM
        </span>
      </div>

      {/* Iter 62 · Fix — grid responsivo em vez de flex+overflow. 5 colunas
          proporcionais com min-width; SOM mais estreito, restantes mais
          largas. Em <1024px scroll interno horizontal com fade indicators. */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent z-10 xl:hidden" />
        <div
          className="overflow-x-auto -mx-1 px-1 pb-2 workflow-scroll"
          data-testid="workflow-row"
        >
          <div
            className="items-stretch gap-2"
            style={{
              display: "grid",
              gridTemplateColumns:
                "minmax(140px, 1fr) minmax(160px, 1.2fr) minmax(160px, 1.2fr) minmax(160px, 1.2fr) minmax(160px, 1.2fr)",
              minWidth: "980px",
              width: "100%",
            }}
          >
            {/* SOM column */}
            <StageColumn
              icon={<FileText className="w-3 h-3" />}
              kind="Obra de Manutenção"
              colTestId="workflow-col-som"
            >
              <Node
                testid={`timeline-node-${som.id}`}
                number={som.number}
                status={som.current_status}
                created_at={som.opened_at}
                updated_at={som.updated_at}
                accent="navy"
              />
            </StageColumn>

            {/* PCP column */}
            <StageColumn
              icon={<ClipboardList className="w-3 h-3" />}
              kind="Classificações de Peças"
              colTestId="workflow-col-pcp"
              emptyLabel="Sem classificação"
              empty={pcps.length === 0}
              arrow={pcps.length > 0 ? "active" : "inactive"}
            >
              {pcps.map((p) => (
                <Node
                  key={p.id}
                  testid={`timeline-node-${p.id}`}
                  number={p.number}
                  status={p.status}
                  created_at={p.created_at}
                  updated_at={p.updated_at}
                  href={`/classification-requests?focus=${p.id}&from=som`}
                  accent="amber"
                  hint={p.alidata_quotation_number ? `Cotação Alidata · ${p.alidata_quotation_number}` : null}
                />
              ))}
            </StageColumn>

            {/* PEC column */}
            <StageColumn
              icon={<PackageSearch className="w-3 h-3" />}
              kind="Encomendas de Peças"
              colTestId="workflow-col-pec"
              emptyLabel="Sem encomenda"
              empty={pecs.length === 0}
              arrow={pecs.length > 0 ? "active" : "inactive"}
            >
              {pecs.map((p) => (
                <Node
                  key={p.id}
                  testid={`timeline-node-${p.id}`}
                  number={p.number}
                  status={p.status}
                  created_at={p.created_at}
                  updated_at={p.updated_at}
                  href={`/parts-requests/${p.id}`}
                  hrefState={{ from: "som" }}
                  accent="green"
                  hint={p.pcp_number ? `originada na ${p.pcp_number}` : null}
                />
              ))}
            </StageColumn>

            {/* SWC column — no arrow (parallel branch off SOM) */}
            <StageColumn
              icon={<ShieldAlert className="w-3 h-3" />}
              kind="Garantia"
              colTestId="workflow-col-swc"
              empty={false}
              parallel
            >
              {swcs.length === 0
                ? <CreateSwcPlaceholder somId={som.id} />
                : swcs.map((s) => <SwcCard key={s.id} swc={s} />)
              }
            </StageColumn>

            {/* MPM column — no arrow */}
            <StageColumn
              icon={<CalendarRange className="w-3 h-3" />}
              kind="Plano de Manutenção"
              colTestId="workflow-col-mpm"
              empty={mpms.length === 0}
              emptyLabel="Sem plano activo"
              parallel
            >
              {mpms.map((p) => <MpmCard key={p.id} plan={p} />)}
            </StageColumn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Generic node accents =====
const ACCENT_BAR = {
  navy: "bg-[#1F3864]",
  amber: "bg-[#B45309]",
  green: "bg-[#28A745]",
  red: "bg-[#DC3545]",
  yellow: "bg-[#FFC107]",
  grey: "bg-[#94A3B8]",
};

// All cards in the workflow share the same minimum height for visual rhythm.
// Iter 62 · Fix — reduzido de 120 para 108 (recupera vertical rhythm depois
// do padding menor). Cards com muito conteúdo (MPM) crescem naturalmente.
const CARD_MIN_HEIGHT = "min-h-[108px]";
// Placeholder vazio (Garantia sem SWC) — muito mais compacto que um card completo.
const EMPTY_PLACEHOLDER_HEIGHT = "min-h-[80px]";

function StageColumn({ icon, kind, colTestId, emptyLabel, empty, parallel, arrow, children }) {
  return (
    <div className="flex flex-col gap-2 min-w-0" data-testid={colTestId}>
      <div
        className="flex items-center gap-1.5 uppercase tracking-widest text-[#6C757D]"
        style={{ fontSize: "10px", letterSpacing: "0.08em" }}
      >
        {icon}
        {/* Iter 62 · Fix — header nunca trunca; pode fazer wrap para 2 linhas */}
        <span className="leading-tight break-words min-w-0">{kind}</span>
        {arrow && (
          <ArrowRight
            className={`w-3 h-3 ml-auto ${arrow === "active" ? "text-[#1F3864]" : "text-[#CBD5E1]"}`}
            aria-hidden="true"
          />
        )}
        {parallel && <span className="ml-auto text-[#CBD5E1]" aria-hidden="true">·</span>}
      </div>
      {empty ? (
        <div
          className={`border border-dashed border-gray-300 rounded-sm px-3 py-2 text-xs text-[#94A3B8] italic ${EMPTY_PLACEHOLDER_HEIGHT} flex items-center justify-center text-center`}
          data-testid={`timeline-empty-${(kind || "").replace(/\s+/g, "-").toLowerCase()}`}
        >
          {emptyLabel || "—"}
        </div>
      ) : children}
    </div>
  );
}

function Node({ testid, number, status, created_at, updated_at, href, hrefState, accent, hint }) {
  const bar = ACCENT_BAR[accent] || ACCENT_BAR.navy;
  const inner = (
    <div className={`flex ${CARD_MIN_HEIGHT}`}>
      <div className={`${bar} w-1 rounded-l-sm`} />
      <div className="flex-1 px-2.5 py-2 border border-gray-200 border-l-0 rounded-r-sm bg-white flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono font-semibold text-sm text-[#1F3864] truncate">{number}</span>
          <StatusBadge status={status} />
        </div>
        {hint && <div className="text-[11px] text-[#6C757D] mt-0.5 break-words" title={hint}>{hint}</div>}
        <div className="flex items-center justify-between mt-auto pt-1 text-[10px] text-[#6C757D] gap-2">
          <span className="truncate">Aberto · {created_at ? fmtRelative(created_at) : "—"}</span>
          {updated_at && updated_at !== created_at && (
            <span className="truncate">Actualizado · {fmtRelative(updated_at)}</span>
          )}
        </div>
      </div>
    </div>
  );
  if (href) {
    return (
      <Link to={href} state={hrefState} className="block hover:shadow-sm transition" data-testid={testid}>
        {inner}
      </Link>
    );
  }
  return <div data-testid={testid} className="block">{inner}</div>;
}

// ===== SWC card =====
// Spec colour rules:
//  • Problem state (Incompleto, Não aprovado, Informação adicional solicitada) → red
//  • In-progress (Submetido, Aguarda resposta) → yellow
//  • Positive (Aprovado, Encerrado, Faturado, Reparação autorizada, Reparação concluída) → green
//  • Other (Evidências em recolha, Cancelado) → grey
const SWC_RED = new Set(["Incompleto", "Não aprovado", "Informação adicional solicitada"]);
const SWC_YELLOW = new Set(["Submetido à marca", "Aguarda resposta da marca"]);
const SWC_GREEN = new Set(["Aprovado", "Reparação autorizada", "Reparação concluída", "Faturado", "Encerrado"]);

function swcAccent(status) {
  if (SWC_RED.has(status)) return "red";
  if (SWC_YELLOW.has(status)) return "yellow";
  if (SWC_GREEN.has(status)) return "green";
  return "grey";
}

function SwcCard({ swc }) {
  const navigate = useNavigate();
  const accent = swcAccent(swc.status);
  const bar = ACCENT_BAR[accent];
  const go = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/warranty-claims/${swc.id}`, {
      state: swc.service_case_id ? { fromSomId: swc.service_case_id } : undefined,
    });
  };
  return (
    <div className={`flex ${CARD_MIN_HEIGHT}`} data-testid={`workflow-swc-card-${swc.id}`}>
      <div className={`${bar} w-1 rounded-l-sm`} />
      <div className="flex-1 px-3 py-2 border border-gray-200 border-l-0 rounded-r-sm bg-white flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-2">
          <a
            href={`/warranty-claims/${swc.id}`}
            onClick={go}
            className="font-mono font-semibold text-sm text-[#1F3864] hover:underline truncate"
            data-testid={`workflow-swc-link-${swc.id}`}
          >
            {swc.number}
          </a>
          <StatusBadge status={swc.status} />
        </div>
        <div className="text-[11px] text-[#6C757D] mt-0.5 truncate" title={`${swc.claim_type || "—"}${swc.responsible_user_name ? " · " + swc.responsible_user_name : ""}`}>
          {swc.claim_type || "—"}
          {swc.responsible_user_name ? ` · ${swc.responsible_user_name}` : ""}
        </div>
        <div className="flex items-center justify-between mt-auto pt-1 text-[10px] text-[#6C757D] gap-2">
          <span className="truncate">Aberto · {swc.created_at ? fmtRelative(swc.created_at) : "—"}</span>
          <a
            href={`/warranty-claims/${swc.id}`}
            onClick={go}
            className="font-semibold uppercase tracking-wider text-[#1F3864] hover:underline shrink-0"
            data-testid={`workflow-swc-open-${swc.id}`}
          >
            Abrir →
          </a>
        </div>
      </div>
    </div>
  );
}

function CreateSwcPlaceholder({ somId }) {
  return (
    <Link
      to={`/warranty-claims?create=1&service_case_id=${somId}`}
      className={`flex items-center justify-center gap-1.5 border border-dashed border-gray-300 rounded-sm px-3 text-xs text-[#1F3864] font-semibold hover:border-[#1F3864] hover:bg-[#F8FAFC] transition ${EMPTY_PLACEHOLDER_HEIGHT}`}
      data-testid="workflow-swc-create-placeholder"
    >
      <Plus className="w-3 h-3" />
      Criar Garantia
    </Link>
  );
}

// ===== MPM card =====
function mpmAccent(plan) {
  const exhausted = (plan.consumed_services || 0) >= (plan.total_services || 0) && (plan.total_services || 0) > 0;
  const expired = plan.days_to_expiry != null && plan.days_to_expiry < 0;
  if (exhausted || expired) return "red";
  if (plan.days_to_expiry != null && plan.days_to_expiry <= 30) return "yellow";
  return "green";
}

function MpmCard({ plan }) {
  const accent = mpmAccent(plan);
  const bar = ACCENT_BAR[accent];
  const total = Math.max(1, plan.total_services || 0);
  const consumed = plan.consumed_services || 0;
  const pct = plan.consumed_pct ?? Math.min(100, Math.round((consumed / total) * 100));
  const days = plan.days_to_expiry;
  const expired = days != null && days < 0;
  const expiringSoon = days != null && days >= 0 && days <= 30;
  const exhausted = consumed >= (plan.total_services || 0) && (plan.total_services || 0) > 0;
  const expiryColor = expired || exhausted ? "text-[#DC3545] font-semibold" : expiringSoon ? "text-[#856404] font-semibold" : "text-[#6C757D]";
  return (
    <div className={`flex ${CARD_MIN_HEIGHT}`} data-testid={`workflow-mpm-card-${plan.id}`}>
      <div className={`${bar} w-1 rounded-l-sm`} />
      <div className="flex-1 px-2.5 py-2 border border-gray-200 border-l-0 rounded-r-sm bg-white flex flex-col min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Link
            to={`/maintenance-plans?open=${plan.id}`}
            className="font-mono font-semibold text-sm text-[#1F3864] hover:underline truncate"
            data-testid={`workflow-mpm-link-${plan.id}`}
          >
            {plan.number}
          </Link>
          <StatusBadge status={plan.status} />
        </div>
        {/* Iter 62 · Fix — texto do MPM pode quebrar; nunca truncar campos
            operacionais (nome do plano, contagem de serviços, validade). */}
        <div className="text-[11px] text-[#1A1A2E] mt-0.5 break-words" title={plan.plan_name || ""}>
          {plan.plan_name || "—"}
        </div>
        <div className="text-[11px] text-[#1A1A2E] break-words">
          <span className="font-semibold">{consumed} / {plan.total_services || 0}</span> serviços
          {plan.next_entitlement && (
            <span className="text-[#6C757D]"> · Próximo: {plan.next_entitlement.label || `#${plan.next_entitlement.sequence_number || ""}`}</span>
          )}
        </div>
        <div className={`text-[10px] break-words ${expiryColor}`}>
          {plan.valid_to ? `Val. ${fmtDate(plan.valid_to)}` : "—"}
          {days != null && (
            <span className="ml-1">
              {expired ? `(expirado há ${Math.abs(days)} d)` :
               expiringSoon ? `(${days} d)` : ""}
            </span>
          )}
        </div>
        <div className="mt-auto pt-1">
          <div className="h-1 w-full bg-[#E2E8F0] rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${exhausted ? "bg-[#DC3545]" : pct >= 75 ? "bg-[#FFC107]" : "bg-[#1F3864]"}`}
              style={{ width: `${Math.min(100, pct)}%` }}
              data-testid={`workflow-mpm-progress-${plan.id}`}
            />
          </div>
          <div className="flex items-center justify-between mt-0.5 text-[10px] text-[#6C757D]">
            <span>{pct}% consumido</span>
            <Link
              to={`/maintenance-plans?open=${plan.id}`}
              className="font-semibold uppercase tracking-wider text-[#1F3864] hover:underline"
              data-testid={`workflow-mpm-open-${plan.id}`}
            >
              Abrir →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
